import React from 'react';
import { ArrowRightIcon } from '@/features/Icons/ArrowRightIcon';
import { useAppOrientation } from '@/lostHooks/orientation/useOrientation';
import './styles/card.scss';
import { GAME_INFO_CARD_TYPES } from '@/utilities/constants/game-help';

export type CardVariant = 'text' | 'image' | 'title';

export interface CardProps {
  /** Card variant: 'text' for text-based cards (GameHelp), 'image' for image-based cards (RecentlyPlayed) */
  variant?: CardVariant;
  /** Title text for text variant */
  title?: string;
  /** Image source for image variant */
  image?: string;
  /** Image alt text for image variant */
  imageAlt?: string;
  /** Callback when image loads (for image variant) */
  onImageLoad?: () => void;
  /** Click handler */
  action: (e: React.MouseEvent<HTMLElement>) => void;
  /** Custom content to render inside the card */
  children?: React.ReactNode;
  /** Url */
  url?: string;
  /** Additional CSS class name */
  className?: string;
  /** Stable automation hook rendered as `data-testid` on the card container. */
  dataTestId?: string;
}

export const Card: React.FC<CardProps & React.HTMLAttributes<HTMLElement>> = ({
  variant = 'text',
  title,
  image,
  imageAlt,
  onImageLoad,
  action,
  children,
  url,
  className = '',
  dataTestId,
  ...rest
}) => {
  const orientation = useAppOrientation();
  const isMobile = orientation === 'portrait' || orientation === 'landscape';
  const orientationClass = orientation || 'desktop';
  const variantClass =
    variant === 'image' ? 'card-image-variant' : 'card-text-variant';

  const cardContent = (
    <div className={`card-content ${orientationClass} ${variantClass}`}>
      {variant === 'image' ? (
        <>
          {image && (
            <img
              src={image}
              alt={imageAlt || title || ''}
              onLoad={onImageLoad}
              className="card-image"
            />
          )}
          {children}
          {title && <span className="card-image-title">{title}</span>}
        </>
      ) : (
        <>
          {title && <p>{title}</p>}
          {variant !== 'title' && <ArrowRightIcon mobile={isMobile} />}
          {children}
        </>
      )}
    </div>
  );

  if (
    variant === 'text' &&
    url &&
    title !== GAME_INFO_CARD_TYPES.HOW_TO_PLAY &&
    title !== GAME_INFO_CARD_TYPES.PAYTABLE
  ) {
    return (
      <a
        className={`card-container ${orientationClass} ${variantClass} ${className} `}
        href={url}
        rel="noreferrer"
        target="_blank"
        data-testid={dataTestId}
        onClick={action}
      >
        {cardContent}
      </a>
    );
  }

  if (variant === 'text' && url) {
    return (
      <div
        className={`card-container ${orientationClass} ${variantClass} ${className}`}
        onClick={action}
        data-testid={dataTestId}
        {...rest}
      >
        {cardContent}
      </div>
    );
  }

  return (
    <div
      className={`card-container ${orientationClass} ${variantClass} ${className}`}
      onClick={action}
      data-testid={dataTestId}
      {...rest}
    >
      {cardContent}
    </div>
  );
};
